import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "./ModalContext.css";

const ModalContext = createContext(null);

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return ctx;
};

const deriveType = (message, explicit) => {
  if (explicit) return explicit;
  const s = String(message || "");
  if (/❌|error|fail|insufficient|invalid|wrong|denied|cannot/i.test(s)) return "error";
  if (/✅|success|added|updated|deleted|submitted|saved/i.test(s)) return "success";
  if (/⚠️|warning|sure|cannot be undone/i.test(s)) return "warning";
  return "info";
};

const ICONS = {
  success: "✓",
  error: "✕",
  warning: "!",
  info: "i",
};

export const ModalProvider = ({ children }) => {
  const [modal, setModal] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((result) => {
    if (resolver.current) {
      const r = resolver.current;
      resolver.current = null;
      r(result);
    }
    setModal(null);
  }, []);

  const alert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setModal({ mode: "alert", message, ...options });
    });
  }, []);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setModal({ mode: "confirm", message, ...options });
    });
  }, []);

  // Close on Escape (cancel for confirm, dismiss for alert)
  useEffect(() => {
    if (!modal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") settle(modal.mode === "confirm" ? false : true);
      if (e.key === "Enter") settle(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modal, settle]);

  const type = modal ? deriveType(modal.message, modal.type) : "info";
  // Strip a leading status emoji so it isn't duplicated next to the icon.
  const displayMessage = modal
    ? String(modal.message || "").replace(/^\s*[✅❌⚠️ℹ️]+\s*/u, "")
    : "";
  const defaultTitle = {
    success: "Success",
    error: "Error",
    warning: "Please confirm",
    info: "Notice",
  };

  return (
    <ModalContext.Provider value={{ alert, confirm }}>
      {children}
      {modal && (
        <div
          className="rv-modal__overlay"
          onClick={() => settle(modal.mode === "confirm" ? false : true)}
        >
          <div
            className={`rv-modal__box rv-modal__box--${type}`}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`rv-modal__icon rv-modal__icon--${type}`}>
              {ICONS[type]}
            </div>

            <h3 className="rv-modal__title">
              {modal.title ||
                (modal.mode === "confirm" ? "Are you sure?" : defaultTitle[type])}
            </h3>

            {displayMessage && (
              <p className="rv-modal__message">{displayMessage}</p>
            )}

            <div className="rv-modal__actions">
              {modal.mode === "confirm" && (
                <button
                  type="button"
                  className="rv-modal__btn rv-modal__btn--ghost"
                  onClick={() => settle(false)}
                >
                  {modal.cancelText || "Cancel"}
                </button>
              )}
              <button
                type="button"
                className={`rv-modal__btn rv-modal__btn--primary${
                  modal.danger || type === "error" ? " rv-modal__btn--danger" : ""
                }`}
                onClick={() => settle(true)}
                autoFocus
              >
                {modal.confirmText ||
                  (modal.mode === "confirm" ? "Confirm" : "OK")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
};
